/**
 * Espace (tenant) supprimé, suspendu ou inconnu : fermer la session locale.
 *
 * PROBLÈME RÉSOLU. Le résolveur de tenant répond `404 tenant_not_found` à
 * TOUTE requête d'un sous-domaine qui n'existe plus (abonné supprimé depuis la
 * console éditeur, ou désactivé). Ce n'est ni un 401 — aucun refresh, aucune
 * redirection — ni une coupure réseau. Le profil `/auth/me/` déjà en cache (et
 * son instantané d'onglet, cf. `offline-session`) restait donc en place :
 * l'AuthGuard laissait entrer, la navbar et les menus s'affichaient, et seules
 * les listes montraient l'erreur. L'utilisateur « gardait l'accès » à un
 * espace qui n'existait plus, jusqu'à la fermeture de l'onglet.
 *
 * Aucune donnée n'était servie (le serveur refuse tout) ; c'est l'interface
 * qui survivait à tort.
 */
import { AxiosError } from "axios";

import { clearOfflineSession } from "@/lib/api/offline-session";
import { queryClient } from "@/lib/api/query-client";

/** Vrai pour le refus du résolveur de tenant, et lui seul : un 404 métier
 *  (fiche introuvable) ne porte pas ce code. */
export function isTenantGone(error: unknown): boolean {
  if (!(error instanceof AxiosError) || error.response?.status !== 404) return false;
  const data: unknown = error.response.data;
  return (
    typeof data === "object" &&
    data !== null &&
    (data as { code?: unknown }).code === "tenant_not_found"
  );
}

/**
 * Oublie la session locale et renvoie vers `/login`, où le message du serveur
 * (« Espace … introuvable ou indisponible. ») s'affiche à la tentative de
 * connexion.
 *
 * Le cache est vidé AVANT la redirection : `LoginPage` renvoie vers le tableau
 * de bord dès qu'un profil est en cache, et l'instantané d'onglet est réécrit
 * au `pagehide` — sans cette purge, la page de connexion rebondirait en boucle.
 */
export function leaveGoneTenant(): void {
  if (typeof window === "undefined" || window.location.pathname.startsWith("/login")) return;
  queryClient.clear();
  clearOfflineSession();
  window.location.href = "/login";
}
